<template>
  <article
    :class="{
      'card io-card base-card': true,
      'tab-card': hasTabsComponent,
      shadow: elevation,
      'accessibility-outline': hasExpansionComponent || iconCardLink,
      'is-loading': loading,
    }"
    :tabindex="hasExpansionComponent || iconCardLink ? 0 : undefined"
    :aria-label="ariaLabel || title"
    :aria-expanded="hasExpansionComponent ? isExpanded : undefined"
    :style="vars"
    @click="!hasExpansionComponent && handleCardLink()"
    @keydown.enter.self.prevent="[
      !hasExpansionComponent && handleCardLink(),
      hasExpansionComponent && setExpanded(!isExpanded),
    ]"
    @keydown.space.self.prevent="[
      !hasExpansionComponent && handleCardLink(),
      hasExpansionComponent && setExpanded(!isExpanded),
    ]"
  >
    <slot name="tabs" />
    <base-image
      v-if="hasImage"
      :url="imageUrl"
      type="image"
      fill
    />
    <BCCHeader
      v-if="renderHeader"
      :title="title"
      :has-expansion-component="hasExpansionComponent"
    >
      <template #header>
        <slot name="header" />
      </template>
      <template #title>
        <slot name="title" />
      </template>
      <template #actions>
        <slot name="actions" />
      </template>
    </BCCHeader>
    <div class="card-body">
      <slot
        name="content"
        :padding-top="cardComponentPadding"
        :padding="padding"
      />
      <slot />
    </div>
    <div
      v-if="loading"
      class="base-card__loader"
    >
      <i class="fa-solid fa-spinner fa-spin-pulse fa-spin-reverse" />
    </div>
  </article>
</template>

<script lang="ts" setup>
import {
  useSlots, computed, ref, onMounted, provide,
} from 'vue';
import { storeToRefs } from 'pinia';

import BCCHeader from '@/components/BCCHeader.vue';

import { useCenterPwaStore } from '@/apps/center/stores/pwa';

const pwaStore = useCenterPwaStore();
const { isDesktop } = storeToRefs(pwaStore);

const props = withDefaults(defineProps<{
  elevation?: boolean;
  tile?: boolean;
  title?: string;
  imageUrl?: string;
  link?: string;
  ariaLabel?: string;
  role?: string;
  loading?: boolean;
}>(), {
  elevation: true,
  title: '',
  imageUrl: '',
  link: '',
  ariaLabel: '',
  role: '',
  loading: false,
});

const {
  header: headerSlot,
  actions: actionsSlot,
  title: titleSlot,
  default: defaultSlot,
} = useSlots();

const computedRole = computed(() => {
  if (props.role) {
    return props.role;
  }
  // If the card has a link, use "link" role for iOS VoiceOver compatibility
  if (props.link) {
    return 'link';
  }
  // Default to "region" for non-interactive cards
  return 'region';
});

const internalExpanded = ref(false);
const isExpanded = ref(false);
const canExpand = ref(false);

const setExpanded = (value: boolean) => {
  internalExpanded.value = value;
  isExpanded.value = value;
};

provide('isExpanded', isExpanded);
provide('canExpand', canExpand);
provide('setExpanded', setExpanded);

const hasImage = ref(!!props.imageUrl);
const hasCardComponent = !!defaultSlot?.().some((item) => item.tag?.match(/BCC/));
const hasExpansionComponent = !!defaultSlot?.().some((item) => item.tag?.match(/BCCExpansion/));
const hasTabsComponent = !!defaultSlot?.().some((item) => item.tag?.match(/BCCTabs/));
const hasCalendarComponent = !!defaultSlot?.().some((item) => item.tag?.match(/BaseCalendar/));

const renderHeader = ref(
  !hasExpansionComponent
  && !hasCalendarComponent
  && (headerSlot || actionsSlot || titleSlot || props.title),
);

const iconCardLink = ref(props.link || '');
const handleCardLink = () => {
  if (iconCardLink.value) {
    window.location.href = iconCardLink.value;
  }
};

onMounted(() => {
  if (hasCardComponent) {
    // @ts-ignore
    window.GlobalEvents.listen('icon-card-link', (link: string) => {
      if (link) {
        iconCardLink.value = link;
      }
    });
  }
});

const cursor = computed(() => {
  if (iconCardLink.value || canExpand.value) {
    return 'pointer';
  }

  return 'default';
});
const borderRadius = computed(() => {
  if (props.tile) {
    return '0';
  }

  if (!isDesktop.value) {
    return '0.75rem';
  }

  return '20px';
});
provide('borderRadius', borderRadius);
const padding = computed(() => {
  if (!isDesktop.value) {
    return '1.25rem';
  }

  if (isDesktop.value) {
    return '1.875rem';
  }

  return '1.25rem';
});
const cardComponentPadding = computed(() => {
  if (hasTabsComponent || hasCalendarComponent) return '0';

  if (hasCardComponent) {
    if (!isDesktop.value) {
      return '1.25rem';
    }

    return '1.875rem';
  }

  return '0';
});
const fontSize = computed(() => {
  if (!isDesktop.value) {
    return '14px';
  }

  return '1.25rem';
});
provide('fontSize', fontSize);
const fontWeight = computed(() => {
  if (!isDesktop.value) {
    return '600';
  }

  return '700';
});
provide('fontWeight', fontWeight);

const vars = computed(() => ({
  '--base-card-overflow': hasTabsComponent ? 'hidden' : 'visible',
  '--base-card-body-overflow': hasTabsComponent ? 'auto' : 'auto',
  '--base-card-body-padding-x': padding.value,
  '--base-card-body-padding-y': padding.value,
  '--base-card-body-padding': `${cardComponentPadding.value} var(--base-card-body-padding-x, 0) var(--base-card-body-padding-y, 0)`,
  '--base-card-header-padding': `${padding.value} ${padding.value} 0`,
  '--base-card-border-radius': borderRadius.value,
  '--base-card-font-size': fontSize.value,
  '--base-card-font-weight': fontWeight.value,
}));

defineExpose({
  computedRole,
  cursor,
  borderRadius,
  padding,
  cardComponentPadding,
  fontSize,
  fontWeight,
  vars,
  setExpanded,
  handleCardLink,
  isExpanded,
  canExpand,
  internalExpanded,
  iconCardLink,
  hasImage,
  renderHeader,
  hasCardComponent,
  hasExpansionComponent,
  hasTabsComponent,
  hasCalendarComponent,
});
</script>

<style lang="scss" scoped>
.base-image {
  margin-bottom: -1rem;
}
.card {
  all: unset;
  display: flex;
  flex-direction: column;
  box-shadow: none;
  gap: 1rem;
  background-color: #fff;
  border-radius: v-bind(borderRadius);
  overflow: var(--base-card-overflow);
  cursor: v-bind(cursor);
  min-width: 0;

  &.shadow {
    box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15) !important;
  }

  // scoped to the loading state so the article's positioning context does not change for existing consumers with absolutely positioned slot content
  &.is-loading {
    position: relative;
    overflow: hidden;
  }

  &-body {
    padding: var(--base-card-body-padding);
    overflow-y: var(--base-card-body-overflow) !important;
    border-radius: 0 0 v-bind(borderRadius);
  }

  > .card {
    .card-header,
    .card-body {
      padding-left: 0;
      padding-right: 0;
    }
  }
}

.base-card__loader {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(255, 255, 255, 0.67);
  backdrop-filter: blur(1px);
  border-radius: v-bind(borderRadius);
  font-size: 3rem;
  z-index: 10;
}

.tab-card > .card-body {
  overflow: hidden;
  padding: 0;
}
</style>
